// ORKSTRA Theme — localStorage persistence with safe fallbacks.
// All reads/writes are wrapped so a hostile sandbox (private-mode Safari,
// quota errors, SSR) never crashes the provider.

import type { ThemePreference } from './types'
import { THEME_STORAGE_KEY } from './types'

const DEFAULT_PREFERENCE: ThemePreference = 'auto'

function isThemePreference(value: unknown): value is ThemePreference {
  return value === 'light' || value === 'dark' || value === 'auto'
}

function getStorage(): Storage | null {
  try {
    if (typeof window === 'undefined') return null
    return window.localStorage
  } catch {
    return null
  }
}

export function loadStoredPreference(): ThemePreference {
  const storage = getStorage()
  if (!storage) return DEFAULT_PREFERENCE
  try {
    const raw = storage.getItem(THEME_STORAGE_KEY)
    return isThemePreference(raw) ? raw : DEFAULT_PREFERENCE
  } catch {
    return DEFAULT_PREFERENCE
  }
}

export function saveStoredPreference(preference: ThemePreference): void {
  const storage = getStorage()
  if (!storage) return
  try {
    storage.setItem(THEME_STORAGE_KEY, preference)
  } catch {
    // localStorage full or blocked — preference still lives in memory.
  }
}

export function clearStoredPreference(): void {
  const storage = getStorage()
  if (!storage) return
  try {
    storage.removeItem(THEME_STORAGE_KEY)
  } catch {
    // ignore
  }
}

export { DEFAULT_PREFERENCE }
